import React, { useState } from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';
import { formatCurrencyWithColor as formatCurrencyWithColorShared } from '../utils/inputFormatters';
import { useCountry } from '../contexts/CountryContext';
import './RetirementResultsTable.css';

interface RetirementResultsTableProps {
  results: any;
  formatCurrency: (value: number) => string;
  chartsRef?: React.RefObject<HTMLDivElement | null>;
}

const RetirementResultsTable: React.FC<RetirementResultsTableProps> = ({
  results,
  formatCurrency,
  chartsRef
}) => {
  const [showAll, setShowAll] = useState(false);
  const [exportingPDF, setExportingPDF] = useState(false);
  const [tableView, setTableView] = useState<'summary' | 'detail'>('summary');
  const { isUSA } = useCountry();

  // Local wrapper for shared color formatting function
  const formatCurrencyWithColor = (value: number, colorType: 'positive' | 'negative' | 'neutral' = 'neutral') => {
    return formatCurrencyWithColorShared(value, colorType, formatCurrency);
  };

  if (!results || !results.representative_scenario || !results.representative_scenario.year_by_year) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
        <p>Click <strong>Calculate</strong> to generate retirement projections.</p>
      </div>
    );
  }

  const yearByYear = results.representative_scenario.year_by_year;
  const displayData = showAll ? yearByYear : yearByYear.slice(0, 10);

  const exportToPDF = async () => {
    setExportingPDF(true);

    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      let currentY = 15;

      // Title
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('Retirement Plan Analysis', pageWidth / 2, currentY, { align: 'center' });
      currentY += 15;

      // Summary Section
      doc.setFontSize(14);
      doc.text('Recommended Strategy', 14, currentY);
      currentY += 8;

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`${isUSA ? 'Social Security' : 'OAS'} Start Age: ${results.suggested_oas_age}`, 14, currentY);
      currentY += 6;
      doc.text(`${isUSA ? 'SS Benefits' : 'CPP'} Start Age: ${results.suggested_cpp_age}`, 14, currentY);
      currentY += 6;
      doc.text(`Median Estate Value: ${formatCurrency(results.percentiles.estate_value.p50)}`, 14, currentY);
      currentY += 6;
      doc.text(`Median Death Age: ${results.percentiles.death_age.p50}`, 14, currentY);
      currentY += 6;
      doc.text(`Plan Reliability: ${((1 - results.distribution_stats.ran_out_probability) * 100).toFixed(0)}%`, 14, currentY);
      currentY += 12;

      // Add charts if available
      if (chartsRef && chartsRef.current) {
        try {
          doc.setFontSize(12);
          doc.setFont('helvetica', 'bold');
          doc.text('Visual Analysis', 14, currentY);
          currentY += 6;

          const canvas = await html2canvas(chartsRef.current, {
            scale: 2,
            backgroundColor: '#ffffff',
            logging: false
          });

          const imgData = canvas.toDataURL('image/png');
          const imgWidth = pageWidth - 28;
          const imgHeight = (canvas.height * imgWidth) / canvas.width;

          // Check if we need a new page
          if (currentY + imgHeight > doc.internal.pageSize.getHeight() - 20) {
            doc.addPage();
            currentY = 15;
          }

          doc.addImage(imgData, 'PNG', 14, currentY, imgWidth, imgHeight);
          currentY = currentY + imgHeight + 10;
        } catch (chartError) {
          console.warn('Could not add charts to PDF:', chartError);
        }
      }

      // Year-by-Year Table
      // Check if we need a new page
      if (currentY > doc.internal.pageSize.getHeight() - 60) {
        doc.addPage();
        currentY = 15;
      }

      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Year-by-Year Projection', 14, currentY);
      currentY += 6;

      const tableData = yearByYear.map((year: any) => [
        year.age.toString(),
        formatCurrency(year.employment_income || 0),
        formatCurrency((year.cpp_income || 0) + (year.oas_final || 0)),
        formatCurrency((year.rrsp_withdrawal || 0) + (year.tfsa_withdrawal || 0)),
        formatCurrency(year.annual_spending || 0),
        formatCurrency(year.taxes_paid || 0),
        formatCurrency((year.balances?.rrsp || 0) + (year.balances?.tfsa || 0) + (year.balances?.non_registered || 0))
      ]);

      autoTable(doc, {
        startY: currentY,
        head: [['Age', 'Employment', 'Govt Benefits', 'Withdrawals', 'Spending', 'Taxes', 'Total Balance']],
        body: tableData,
        theme: 'grid',
        headStyles: { fillColor: [102, 126, 234], fontSize: 9 },
        bodyStyles: { fontSize: 8 },
        columnStyles: {
          0: { cellWidth: 15 },
          1: { cellWidth: 28 },
          2: { cellWidth: 28 },
          3: { cellWidth: 28 },
          4: { cellWidth: 25 },
          5: { cellWidth: 22 },
          6: { cellWidth: 30 }
        },
        margin: { top: 10, right: 14, bottom: 20, left: 14 },
        didDrawPage: (data: any) => {
          // Footer
          doc.setFontSize(8);
          doc.setTextColor(128);
          doc.text(
            `Generated by Larry's Help - Page ${doc.getCurrentPageInfo().pageNumber}`,
            pageWidth / 2,
            doc.internal.pageSize.getHeight() - 10,
            { align: 'center' }
          );
        }
      });

      // Percentiles Summary on last page
      const finalY = (doc as any).lastAutoTable.finalY + 10;
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0);
      doc.text('Outcome Ranges (Percentiles)', 14, finalY);

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Estate Value - 25th: ${formatCurrency(results.percentiles.estate_value.p25)}`, 14, finalY + 8);
      doc.text(`Estate Value - 50th: ${formatCurrency(results.percentiles.estate_value.p50)}`, 14, finalY + 14);
      doc.text(`Estate Value - 75th: ${formatCurrency(results.percentiles.estate_value.p75)}`, 14, finalY + 20);

      doc.text(`Death Age - 25th: ${results.percentiles.death_age.p25}`, 14, finalY + 28);
      doc.text(`Death Age - 50th: ${results.percentiles.death_age.p50}`, 14, finalY + 34);
      doc.text(`Death Age - 75th: ${results.percentiles.death_age.p75}`, 14, finalY + 40);

      // Save PDF
      doc.save('retirement-plan-analysis.pdf');
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    } finally {
      setExportingPDF(false);
    }
  };

  return (
    <div className="retirement-table-container">
      <div className="table-header">
        <h3>📋 Year-by-Year Breakdown</h3>
        <div className="table-actions">
          <button
            onClick={() => setTableView('summary')}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: tableView === 'summary' ? '#3b82f6' : '#f3f4f6',
              color: tableView === 'summary' ? 'white' : '#374151',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: '500',
              marginRight: '0.5rem'
            }}
          >
            Summary
          </button>
          <button
            onClick={() => setTableView('detail')}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: tableView === 'detail' ? '#3b82f6' : '#f3f4f6',
              color: tableView === 'detail' ? 'white' : '#374151',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: '500',
              marginRight: '0.5rem'
            }}
          >
            Detail
          </button>
          <button className="btn-export" onClick={exportToPDF} disabled={exportingPDF}>
            {exportingPDF ? '⏳ Generating PDF...' : '📄 Export to PDF'}
          </button>
          <button className="btn-toggle" onClick={() => setShowAll(!showAll)}>
            {showAll ? '📊 Show Less' : `📊 Show All (${yearByYear.length} years)`}
          </button>
        </div>
      </div>

      <div className="table-wrapper">
        <table className="retirement-table">
          <thead>
            <tr>
              <th>Age</th>
              {tableView === 'summary' ? (
                <>
                  <th>Total Income</th>
                  <th>Spending</th>
                  <th>Taxes</th>
                  <th>Total Balance</th>
                </>
              ) : (
                <>
                  <th>Employment</th>
                  <th>{isUSA ? 'Soc. Sec.' : 'CPP'}</th>
                  <th>{isUSA ? '' : 'OAS'}</th>
                  <th>{isUSA ? '401(k)' : 'RRSP'}</th>
                  <th>{isUSA ? 'Roth' : 'TFSA'}</th>
                  <th>Spending</th>
                  <th>Taxes</th>
                  <th>{isUSA ? '401(k) Bal' : 'RRSP Bal'}</th>
                  <th>{isUSA ? 'Roth Bal' : 'TFSA Bal'}</th>
                  <th>LIF Bal</th>
                  <th>Non-Reg Bal</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {displayData.map((year: any, idx: number) => {
              const totalIncome = (year.employment_income || 0) + (year.cpp_income || 0) +
                (year.oas_final || 0) + (year.rrsp_withdrawal || 0) + (year.tfsa_withdrawal || 0);
              const totalBalance = (year.balances?.rrsp || 0) + (year.balances?.tfsa || 0) + (year.balances?.lif || 0) + (year.balances?.non_registered || 0);
              const isRetired = (year.employment_income || 0) === 0 && idx > 0;

              return (
                <tr key={idx} className={isRetired ? 'retired-row' : ''}>
                  <td className="age-cell">{year.age}</td>
                  {tableView === 'summary' ? (
                    <>
                      <td className="currency-cell">{formatCurrencyWithColor(totalIncome, 'positive')}</td>
                      <td className="currency-cell spending">{formatCurrencyWithColor(year.annual_spending || 0, 'negative')}</td>
                      <td className="currency-cell taxes">{formatCurrencyWithColor(year.taxes_paid || 0, 'negative')}</td>
                      <td className="currency-cell balance">{formatCurrency(totalBalance)}</td>
                    </>
                  ) : (
                    <>
                      <td className="currency-cell">{formatCurrencyWithColor(year.employment_income || 0, 'positive')}</td>
                      <td className="currency-cell govt-benefit">{formatCurrencyWithColor(year.cpp_income || 0, 'positive')}</td>
                      <td className="currency-cell govt-benefit">{formatCurrencyWithColor(year.oas_final || 0, 'positive')}</td>
                      <td className="currency-cell withdrawal">{formatCurrencyWithColor(year.rrsp_withdrawal || 0, 'positive')}</td>
                      <td className="currency-cell withdrawal">{formatCurrencyWithColor(year.tfsa_withdrawal || 0, 'positive')}</td>
                      <td className="currency-cell spending">{formatCurrencyWithColor(year.annual_spending || 0, 'negative')}</td>
                      <td className="currency-cell taxes">{formatCurrencyWithColor(year.taxes_paid || 0, 'negative')}</td>
                      <td className="currency-cell balance">{formatCurrency(year.balances?.rrsp || 0)}</td>
                      <td className="currency-cell balance">{formatCurrency(year.balances?.tfsa || 0)}</td>
                      <td className="currency-cell balance">{formatCurrency(year.balances?.lif || 0)}</td>
                      <td className="currency-cell balance">{formatCurrency(year.balances?.non_registered || 0)}</td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {!showAll && yearByYear.length > 10 && (
        <div className="table-footer">
          <p>Showing first 10 years of {yearByYear.length} total years</p>
          <button className="btn-show-more" onClick={() => setShowAll(true)}>
            Show All {yearByYear.length} Years →
          </button>
        </div>
      )}

      {/* Color Legend */}
      <div className="table-legend">
        <div className="legend-item">
          <span className="legend-color govt-benefit"></span>
          <span>Government Benefits</span>
        </div>
        <div className="legend-item">
          <span className="legend-color withdrawal"></span>
          <span>Withdrawals</span>
        </div>
        <div className="legend-item">
          <span className="legend-color spending"></span>
          <span>Spending</span>
        </div>
        <div className="legend-item">
          <span className="legend-color taxes"></span>
          <span>Taxes</span>
        </div>
        <div className="legend-item">
          <span className="legend-color balance"></span>
          <span>Account Balances</span>
        </div>
      </div>
    </div>
  );
};

export default RetirementResultsTable;
